import { Formik, FormikHelpers } from "formik";
import { FC, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Navigate } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import yup from "app/util/yup";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import { companyUsersRoute, editCompanyUserRoute } from "app/route/Routes";
import { useRouteParams } from "app/route/route";
import { CompanyRole, useCompanyUserQuery, useUpdateCompanyUserMutation } from "app/api/graph/types";
import { companyUserGraphId } from "app/api/graph/helpers";
import { handleError, handleSuccess } from "app/api/graph/form";
import { StatusAlert } from "app/component/form/StatusAlert";
import { FormControlSelectCompanyRole } from "app/component/form/control/FormControlSelectCompanyRole";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { AllCompanyRolePermissionsOverviewCard } from "app/component/company/AllCompanyRolePermissionsOverviewCard";

type FormValues = {
    role: CompanyRole;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    role: yup.mixed<CompanyRole>().oneOf(Object.values(CompanyRole)).required(),
});

export const EditCompanyUserScreen: FC = () => {
    const { companyId, companyUserId } = useRouteParams(editCompanyUserRoute);
    const { data, loading, error } = useCompanyUserQuery({
        variables: { companyUserId: companyUserGraphId(companyUserId) },
    });
    const companyUser = data?.companyUser;
    const [updateCompanyUser, { data: updateData }] = useUpdateCompanyUserMutation({});

    const initialValues: FormValues = useMemo(
        () => ({
            role: companyUser?.role ?? CompanyRole.Standard,
        }),
        [companyUser],
    );

    if (updateData?.updateCompanyUser) {
        return <Navigate to={companyUsersRoute.build({ companyId })} />;
    }

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!companyUser) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateCompanyUser({ variables: { input: { id: companyUserGraphId(companyUserId), role: values.role } } })
            .then(() => handleSuccess(actions))
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={companyUsersRoute} />
                <h1>Update company user</h1>
                <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
                    {({
                        handleSubmit,
                        setFieldValue,
                        setFieldTouched,
                        values,
                        isSubmitting,
                        errors,
                        touched,
                        status,
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <StatusAlert status={status as unknown} />
                            <Form.Group className="mb-3">
                                <Row>
                                    <Col>
                                        <Form.Label>Role</Form.Label>
                                        <FormControlSelectCompanyRole
                                            name="role"
                                            value={values.role}
                                            onChange={(value) => setFieldValue("role", value)}
                                            onBlur={() => setFieldTouched("role")}
                                            isInvalid={touched.role && !!errors.role}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.role}</Form.Control.Feedback>
                                    </Col>
                                    <Col>
                                        <AllCompanyRolePermissionsOverviewCard />
                                    </Col>
                                </Row>
                            </Form.Group>
                            <Button type="submit" disabled={isSubmitting}>
                                Submit
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Container>
        </NavigationScreen>
    );
};
